import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../App";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Plus, Edit, Archive, RefreshCw, X, User, Mail, Phone, Search, FileText, ArrowRight, KeyRound, Trash2 } from "lucide-react";
import { STATUS_COLORS, STATUS_LABELS } from "./shared";

export default function DriversTab({ canEdit = false, onDeleteDriver = null }) {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [search, setSearch] = useState("");
  const [editingDriver, setEditingDriver] = useState(null);
  const [creating, setCreating] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/drivers?include_archived=${showArchived}`);
      setDrivers(res.data);
    } catch (_e) {
      toast.error("Failed to load drivers");
    } finally { setLoading(false); }
  }, [showArchived]);

  useEffect(() => { load(); }, [load]);

  const archive = async (driverId) => {
    if (!window.confirm("Archive this driver?")) return;
    try {
      await axios.patch(`${API}/admin/drivers/${driverId}/archive`);
      toast.success("Driver archived"); load();
    } catch (_e) { toast.error("Failed"); }
  };

  const restore = async (driverId) => {
    try {
      await axios.patch(`${API}/admin/drivers/${driverId}/restore`);
      toast.success("Driver restored"); load();
    } catch (_e) { toast.error("Failed"); }
  };

  const grantLogin = async (driverId, email) => {
    if (!window.confirm(`Generate a new password for ${email} and email it to them? The driver can sign in at /driver/login.`)) return;
    try {
      await axios.post(`${API}/admin/drivers/${driverId}/grant-login`);
      toast.success(`Login emailed to ${email}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to grant login");
    }
  };

  const filtered = drivers.filter(d => {
    if (!search) return true;
    const q = search.toLowerCase();
    return d.name?.toLowerCase().includes(q) || d.surname?.toLowerCase().includes(q) || d.email?.toLowerCase().includes(q) || d.mobile?.includes(q);
  });

  return (
    <div data-testid="drivers-tab">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
        <h2 className="text-xl font-semibold text-white">Drivers ({filtered.length})</h2>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search drivers..." className="input-dark pl-10 w-56" data-testid="driver-search" />
          </div>
          <label className="flex items-center gap-2 text-white/50 text-xs">
            <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} className="accent-[#C5A059]" />
            Show archived
          </label>
          {canEdit && (
            <Button onClick={() => setCreating(true)} className="btn-gold" size="sm" data-testid="add-driver-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Driver
            </Button>
          )}
        </div>
      </div>

      {loading ? <div className="flex justify-center py-20"><div className="spinner" /></div> : (
        filtered.length === 0 ? (
          <p className="text-white/40 text-center py-12">No drivers yet. {canEdit && "Add one to get started."}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table" data-testid="drivers-table">
              <thead>
                <tr><th>Name</th><th>Email</th><th>Mobile</th><th>Transfers</th><th>Status</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {filtered.map(d => (
                  <tr key={d.driver_id} className={d.status === "archived" ? "opacity-50" : "cursor-pointer hover:bg-white/5"}
                    onClick={() => setSelectedDriver(d.driver_id)}
                    data-testid={`driver-row-${d.driver_id}`}>
                    <td>
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-[#C5A059]/20 flex items-center justify-center overflow-hidden">
                          {d.picture_url ? (
                            <img src={d.picture_url} alt={d.name} className="w-full h-full object-cover" />
                          ) : (
                            <User className="w-4 h-4 text-[#C5A059]" />
                          )}
                        </div>
                        <div>
                          <div className="text-white text-sm">{d.name} {d.surname}</div>
                          <div className="text-white/30 text-[10px] font-mono">{d.driver_id}</div>
                        </div>
                      </div>
                    </td>
                    <td className="text-white/60 text-xs">{d.email}</td>
                    <td className="text-white/60 text-xs">{d.mobile}</td>
                    <td className="text-white/80 font-mono">{d.transfers_count || 0}</td>
                    <td>
                      <span className={`px-2 py-0.5 text-[10px] uppercase rounded ${d.status === "archived" ? "bg-white/5 text-white/30" : "bg-emerald-500/20 text-emerald-400"}`}>
                        {d.status || "active"}
                      </span>
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-1">
                        {canEdit && d.status !== "archived" && (
                          <button onClick={() => setEditingDriver(d)} className="p-1.5 text-white/40 hover:text-[#C5A059]" data-testid={`edit-driver-${d.driver_id}`}>
                            <Edit className="w-4 h-4" />
                          </button>
                        )}
                        {canEdit && d.status === "archived" && (
                          <button onClick={() => restore(d.driver_id)} className="p-1.5 text-white/40 hover:text-emerald-400" title="Restore">
                            <RefreshCw className="w-4 h-4" />
                          </button>
                        )}
                        {canEdit && d.status !== "archived" && (
                          <button onClick={() => grantLogin(d.driver_id, d.email)} className="p-1.5 text-white/40 hover:text-emerald-400" title="Grant / reset login & email password" data-testid={`grant-login-${d.driver_id}`}>
                            <KeyRound className="w-4 h-4" />
                          </button>
                        )}
                        {canEdit && d.status !== "archived" && (
                          <button onClick={() => archive(d.driver_id)} className="p-1.5 text-white/40 hover:text-red-400" title="Archive" data-testid={`archive-driver-${d.driver_id}`}>
                            <Archive className="w-4 h-4" />
                          </button>
                        )}
                        {onDeleteDriver && (
                          <button onClick={() => onDeleteDriver(d)} className="p-1.5 text-white/40 hover:text-red-500" title="Delete driver (irreversible)" data-testid={`delete-driver-${d.driver_id}`}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {(creating || editingDriver) && (
        <DriverFormModal
          driver={editingDriver}
          onClose={() => { setCreating(false); setEditingDriver(null); }}
          onSuccess={() => { setCreating(false); setEditingDriver(null); load(); }}
        />
      )}
      {selectedDriver && (
        <DriverDetailModal driverId={selectedDriver} onClose={() => setSelectedDriver(null)} />
      )}
    </div>
  );
}

function DriverFormModal({ driver, onClose, onSuccess }) {
  const isEdit = !!driver;
  const [form, setForm] = useState({
    name: driver?.name || "",
    surname: driver?.surname || "",
    email: driver?.email || "",
    mobile: driver?.mobile || "",
    picture_url: driver?.picture_url || "",
    notes: driver?.notes || ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    if (file.size > 800 * 1024) { toast.error("Image too large (max 800 KB). Try a smaller picture or use a URL."); return; }
    setUploading(true);
    const reader = new FileReader();
    reader.onload = () => {
      setForm(p => ({ ...p, picture_url: reader.result }));
      setUploading(false);
    };
    reader.onerror = () => { toast.error("Failed to read image"); setUploading(false); };
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.surname || !form.email || !form.mobile) { toast.error("All fields except notes & picture are required"); return; }
    setSubmitting(true);
    try {
      const payload = { ...form, picture_url: form.picture_url || null };
      if (isEdit) {
        await axios.put(`${API}/admin/drivers/${driver.driver_id}`, payload);
        toast.success("Driver updated");
      } else {
        await axios.post(`${API}/admin/drivers`, payload);
        toast.success("Driver added");
      }
      onSuccess();
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Failed");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="driver-form-modal">
      <div className="bg-[#121212] border border-white/10 rounded-sm w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-white/5">
          <h3 className="text-lg font-semibold text-white">{isEdit ? "Edit Driver" : "Add Driver"}</h3>
          <button onClick={onClose} className="text-white/40 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          {/* Picture */}
          <div>
            <label className="text-white/50 text-xs mb-2 block">Profile picture (optional)</label>
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-[#1F1F1F] border border-white/10 overflow-hidden flex items-center justify-center shrink-0">
                {form.picture_url ? (
                  <img src={form.picture_url} alt="preview" className="w-full h-full object-cover" data-testid="driver-picture-preview" />
                ) : (
                  <User className="w-7 h-7 text-white/30" />
                )}
              </div>
              <div className="flex-1 space-y-2">
                <Input
                  type="url"
                  value={form.picture_url?.startsWith("data:") ? "" : (form.picture_url || "")}
                  onChange={(e) => setForm(p => ({ ...p, picture_url: e.target.value }))}
                  placeholder="https://... (paste URL)"
                  className="input-dark text-xs"
                  data-testid="driver-picture-url"
                  disabled={form.picture_url?.startsWith("data:")}
                />
                <div className="flex items-center gap-2">
                  <label className="cursor-pointer text-[11px] text-[#C5A059] hover:underline" data-testid="driver-picture-upload-label">
                    <input type="file" accept="image/*" onChange={handleFile} className="hidden" data-testid="driver-picture-file" />
                    {uploading ? "Uploading..." : "Upload from device"}
                  </label>
                  {form.picture_url && (
                    <button type="button" onClick={() => setForm(p => ({ ...p, picture_url: "" }))} className="text-[11px] text-red-400 hover:underline" data-testid="driver-picture-clear">
                      Remove
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="text-white/50 text-xs mb-1 block">First Name *</label><Input value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))} className="input-dark" data-testid="driver-name" /></div>
            <div><label className="text-white/50 text-xs mb-1 block">Surname *</label><Input value={form.surname} onChange={(e) => setForm(p => ({ ...p, surname: e.target.value }))} className="input-dark" data-testid="driver-surname" /></div>
          </div>
          <div><label className="text-white/50 text-xs mb-1 block">Email *</label><Input type="email" value={form.email} onChange={(e) => setForm(p => ({ ...p, email: e.target.value }))} className="input-dark" data-testid="driver-email" /></div>
          <div><label className="text-white/50 text-xs mb-1 block">Mobile *</label><Input type="tel" value={form.mobile} onChange={(e) => setForm(p => ({ ...p, mobile: e.target.value }))} className="input-dark" data-testid="driver-mobile" /></div>
          <div><label className="text-white/50 text-xs mb-1 block">Notes (optional)</label><Input value={form.notes} onChange={(e) => setForm(p => ({ ...p, notes: e.target.value }))} className="input-dark" /></div>
          <Button type="submit" className="btn-gold w-full" disabled={submitting} data-testid="driver-form-submit">
            {submitting ? "Saving..." : isEdit ? "Update" : "Add Driver"}
          </Button>
        </form>
      </div>
    </div>
  );
}

function DriverDetailModal({ driverId, onClose }) {
  const [driver, setDriver] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/admin/drivers/${driverId}`)
      .then(res => setDriver(res.data))
      .catch(() => { toast.error("Failed to load"); onClose(); })
      .finally(() => setLoading(false));
  }, [driverId, onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="driver-detail-modal">
      <div className="bg-[#121212] border border-white/10 rounded-sm w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-white/5">
          <h3 className="text-lg font-semibold text-white">Driver Profile</h3>
          <button onClick={onClose} className="text-white/40 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        {loading || !driver ? <div className="p-20 flex justify-center"><div className="spinner" /></div> : (
          <div className="p-6 space-y-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-[#C5A059]/20 flex items-center justify-center overflow-hidden">
                {driver.picture_url ? (
                  <img src={driver.picture_url} alt={driver.name} className="w-full h-full object-cover" data-testid="driver-detail-picture" />
                ) : (
                  <User className="w-8 h-8 text-[#C5A059]" />
                )}
              </div>
              <div>
                <h4 className="text-2xl text-white">{driver.name} {driver.surname}</h4>
                <p className="text-white/50 text-xs flex items-center gap-1 mt-1"><Mail className="w-3 h-3" /> {driver.email}</p>
                <p className="text-white/50 text-xs flex items-center gap-1"><Phone className="w-3 h-3" /> {driver.mobile}</p>
              </div>
            </div>

            {driver.notes && (
              <div className="bg-[#151515] border border-white/5 p-3 rounded-sm">
                <p className="text-white/40 text-xs uppercase mb-1">Notes</p>
                <p className="text-white/70 text-sm">{driver.notes}</p>
              </div>
            )}

            <div>
              <h5 className="text-white/60 text-xs uppercase mb-3 flex items-center gap-2">
                <FileText className="w-4 h-4" /> Transfers ({driver.transfers?.length || 0})
              </h5>
              {!driver.transfers || driver.transfers.length === 0 ? (
                <p className="text-white/30 text-sm">No transfers assigned yet.</p>
              ) : (
                <div className="space-y-2">
                  {driver.transfers.map(t => (
                    <div key={t.quote_id} className="bg-[#151515] border border-white/5 p-3 rounded-sm flex items-center justify-between gap-3 flex-wrap">
                      <div>
                        <div className="flex items-center gap-2 text-white text-sm">
                          {t.origin} <ArrowRight className="w-3 h-3 text-[#C5A059]" /> {t.destination}
                        </div>
                        <p className="text-white/40 text-xs">{t.pickup_date} at {t.pickup_time}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 text-[10px] uppercase rounded border ${STATUS_COLORS[t.status] || ""}`}>{STATUS_LABELS[t.status] || t.status}</span>
                        <span className="text-[#C5A059] font-mono text-sm">{"\u20AC"}{t.total_price?.toFixed(2)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
